/**
 * App-wide Mercure Subscriptions Configuration
 *
 * Each subscription defines:
 * - id:          Unique identifier for logging/debugging
 * - topic:       Topic pattern with :param placeholders (resolved at runtime)
 * - description: Human-readable description (dev tooling / logging)
 * - handler:     Async function receiving a deps object
 *
 * Handler deps: { client, userId, data, queryClient, toast, logout }
 *
 * Architecture note:
 * This file owns ALL real-time side-effects. Never put Mercure subscriptions
 * inside React hooks or components — AppMercureSubscriber wires everything here.
 */

import { queryKeys } from "@lib/api/queryKeys";
import { STORAGE_KEYS } from "@lib/config/storageKeys";
import type { ToastFn } from "@lib/hooks/useToast";
import { normalizePermissionContext } from "@lib/permissions/parser";
import type { PermissionsSummary } from "@lib/permissions/summary.types";
import type { NotificationRecord } from "@lib/queries/notificationQueries";
import { fetchPermissionsForUserById } from "@lib/queries/permissionQueries";
import { usePermissionStore } from "@lib/stores/permissions.store";
import type { QueryClient } from "@tanstack/react-query";

// ─── Shared types ──────────────────────────────────────────────────────────────

export interface Subscription {
  id: string;
  topic: string;
  description: string;
  handler: (deps: HandlerDeps) => Promise<void>;
}

interface HandlerDeps {
  client: unknown;
  userId: string | number;
  data: Record<string, unknown>;
  queryClient: QueryClient;
  toast: ToastFn;
  logout: () => void;
}

// ─── Notification settings helper ─────────────────────────────────────────────

interface NotificationUISettings {
  sound: boolean;
  duration: number;
  showToast: boolean;
}

const DEFAULT_NOTIFICATION_SETTINGS: NotificationUISettings = {
  sound: true,
  duration: 3000,
  showToast: true,
};

/**
 * Read notification UI preferences from the persisted Zustand store.
 * Safe to call outside React — reads localStorage directly.
 */
const getNotificationSettings = (): NotificationUISettings => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.UI.SIDEBAR);
    if (!stored) return DEFAULT_NOTIFICATION_SETTINGS;
    const parsed = JSON.parse(stored);
    return {
      ...DEFAULT_NOTIFICATION_SETTINGS,
      ...(parsed?.state?.settings?.notifications ?? {}),
    };
  } catch {
    return DEFAULT_NOTIFICATION_SETTINGS;
  }
};

// ─── Sound helper ──────────────────────────────────────────────────────────────

type SoundType = "info" | "success" | "warning" | "error";

const NOTIFICATION_SOUND_MAP: Record<string, SoundType> = {
  error: "error",
  critical: "error",
  warning: "warning",
  success: "success",
};

const playSound = async (type: string): Promise<void> => {
  const { playNotificationSound, isAudioSupported } = await import("@lib/utils/sound");
  if (!isAudioSupported()) return;
  const soundType: SoundType = NOTIFICATION_SOUND_MAP[type] ?? "info";
  playNotificationSound(soundType);
};

// ─── Toast helper ─────────────────────────────────────────────────────────────

const showNotificationToast = (
  notification: NotificationRecord,
  toast: HandlerDeps["toast"],
  duration: number,
): void => {
  if (!notification.title) return;

  const options = {
    message: notification.message ?? undefined,
    duration,
    ...(notification.actionUrl
      ? {
          actions: [
            {
              label: "Voir",
              onClick: () => {
                window.location.href = notification.actionUrl as string;
              },
            },
          ],
        }
      : {}),
  };

  switch (notification.type) {
    case "success":
      toast.success(notification.title, options);
      break;
    case "error":
    case "critical":
      toast.error(notification.title, options);
      break;
    case "warning":
      toast.warning(notification.title, options);
      break;
    default:
      toast.info(notification.title, options);
  }
};

// ─── Cache helpers ─────────────────────────────────────────────────────────────

/**
 * Prepend a new notification to every cached list that would include it.
 * Skips lists filtered by isRead=true (new notifications are always unread).
 * Deduplicates by id to be idempotent.
 */
const prependNotificationToLists = (
  notification: NotificationRecord,
  queryClient: QueryClient,
): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;

      // Don't prepend to lists explicitly filtered to isRead=true
      const keyString = JSON.stringify(queryKey);
      if (keyString.includes('"isRead":true')) return;

      // Deduplicate
      if (cachedData.some((n) => n.id === notification.id)) return;

      queryClient.setQueryData<NotificationRecord[]>(queryKey, [notification, ...cachedData]);
    });
};

/**
 * Optimistically update a single notification across all cached lists.
 * Used when a notification is mutated in another session via Mercure.
 */
const updateNotificationInLists = (
  updated: Partial<NotificationRecord> & { id: number },
  queryClient: QueryClient,
): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;
      queryClient.setQueryData<NotificationRecord[]>(
        queryKey,
        cachedData.map((n) => (n.id === updated.id ? { ...n, ...updated } : n)),
      );
    });
};

/**
 * Remove a notification from every cached list.
 * Used on soft-delete Mercure events.
 */
const removeNotificationFromLists = (id: number, queryClient: QueryClient): void => {
  queryClient
    .getQueriesData<NotificationRecord[]>({ queryKey: queryKeys.notifications.lists() })
    .forEach(([queryKey, cachedData]) => {
      if (!Array.isArray(cachedData)) return;
      queryClient.setQueryData<NotificationRecord[]>(
        queryKey,
        cachedData.filter((n) => n.id !== id),
      );
    });
};

// ─── Subscriptions ─────────────────────────────────────────────────────────────

export const APP_SUBSCRIPTIONS = [
  // ── User ──────────────────────────────────────────────────────────────────────
  {
    id: "user",
    topic: "/api/users/:userId",
    description: "User data changes (update, disable, password change)",
    handler: async ({ userId, data, queryClient, toast, logout }: HandlerDeps) => {
      switch (data?.type) {
        case "user.updated":
          queryClient.invalidateQueries({ queryKey: queryKeys.users.detail(userId) });
          toast.info("Vos informations ont été mises à jour.");
          break;

        case "user.disabled":
          toast.error("Votre compte a été désactivé. Déconnexion...");
          logout();
          break;

        case "user.password_changed":
          toast.info("Votre mot de passe a été modifié.");
          break;

        default:
          break;
      }
    },
  },

  // ── Permissions ───────────────────────────────────────────────────────────────
  {
    id: "permissions",
    topic: "/api/users/:userId/permissions",
    description:
      "Permission updates (granted, revoked, updated) — fetches fresh data and syncs store",
    handler: async ({ userId, data, queryClient, toast, logout }: HandlerDeps) => {
      /**
       * Fetch fresh permissions from the API and update the Zustand store.
       * This is the authoritative path — TanStack Query invalidation is a
       * secondary signal for components that read from the query cache.
       */
      const syncPermissionsStore = async () => {
        try {
          const freshData = await fetchPermissionsForUserById(userId);
          const normalizedContext = normalizePermissionContext(freshData.context ?? {});

          usePermissionStore.getState().setPermissions({
            permissions: (freshData.permissionsArray ?? []) as readonly string[],
            hasWildcard:
              freshData.hasWildcard ?? freshData.permissionsArray?.includes("*") ?? false,
            context: normalizedContext,
            summary: (freshData.summary as PermissionsSummary) ?? null,
          });
        } catch (error) {
          console.error("[Mercure] Failed to sync permissions store:", error);
        }
      };

      switch (data?.type) {
        case "permissions.updated":
          toast.info("Permissions mises à jour.");
          await syncPermissionsStore();
          break;

        case "permissions.granted":
          toast.success("De nouveaux accès vous ont été accordés.");
          await syncPermissionsStore();
          break;

        case "permissions.revoked":
          toast.error("Certains de vos accès ont été révoqués.");
          await syncPermissionsStore();
          break;

        case "user.disabled":
          toast.error("Votre compte a été désactivé. Déconnexion...");
          logout();
          break;

        default:
          break;
      }
    },
  },

  // ── Notifications ─────────────────────────────────────────────────────────────
  {
    id: "notifications",
    topic: "/api/notifications/:userId",
    description: "Real-time notification events (created, updated, deleted)",
    handler: async ({ data, queryClient, toast }: HandlerDeps) => {
      if (!data?.id) return;

      const notification = data as unknown as NotificationRecord;
      const settings = getNotificationSettings();

      // API Platform Mercure publishes the full serialized entity on mutation.
      // We disambiguate events by an explicit `eventType` field. If the backend
      // doesn't publish this field yet, we fall back to "notification.created"
      // so existing behaviour is preserved.
      const eventType =
        typeof data.eventType === "string" ? data.eventType : "notification.created";

      switch (eventType) {
        // ── New notification ────────────────────────────────────────────────────
        case "notification.created": {
          // 1. Optimistically prepend to relevant cached lists
          prependNotificationToLists(notification, queryClient);

          // 2. Invalidate the badge count — let the server be authoritative
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          // 3. Sound
          if (settings.sound) {
            await playSound(notification.type ?? "info");
          }

          // 4. Toast
          if (settings.showToast !== false) {
            showNotificationToast(notification, toast, settings.duration);
          }

          break;
        }

        // ── Read/unread toggled in another session ──────────────────────────────
        case "notification.updated": {
          updateNotificationInLists(
            {
              id: notification.id,
              isRead: notification.isRead,
              readAt: notification.readAt,
            },
            queryClient,
          );

          // Re-sync badge — we can't safely delta it without knowing the
          // previous isRead state across all cached lists
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          break;
        }

        // ── Soft-deleted ────────────────────────────────────────────────────────
        case "notification.deleted": {
          removeNotificationFromLists(notification.id, queryClient);

          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.unreadCount(),
          });

          break;
        }

        // ── Unknown event type ──────────────────────────────────────────────────
        default: {
          // Safe fallback: full invalidation rather than silently dropping
          queryClient.invalidateQueries({
            queryKey: queryKeys.notifications.all,
          });
          break;
        }
      }
    },
  },
];

export default APP_SUBSCRIPTIONS;

// ─── Workflow Subscriptions ───────────────────────────────────────────────────
// Dynamic subscriptions based on user permissions (modules + accessible filiales).
// Topics follow the pattern: /api/workflow/{resource}/{filiale_code}

/**
 * Module → Mercure resource mapping for workflow subscriptions.
 * Keys are permission module names; values are backend workflow resource identifiers.
 */
export const MODULE_WORKFLOW_RESOURCES: Record<string, string[]> = {
  rh: ["demande_absences", "feuille_de_temps", "contrats"],
  ventes: ["commande_clients", "facture_clients", "bon_expeditions"],
  achats: ["bon_receptions", "facture_fournisseurs"],
  stock: ["inventaires"],
  production: ["ordres_fabrications"],
};

/**
 * Handler for workflow Mercure events.
 * Invalidates query caches for the affected resource and logs transitions in dev.
 */
const workflowHandler = async ({ data, queryClient }: HandlerDeps) => {
  if (!data) return;

  const eventType = (data as Record<string, unknown>).type as string | undefined;
  const resource = (data as Record<string, unknown>).resource as string | undefined;

  if (!eventType || !resource) return;

  // Generic invalidation: invalidate all query keys containing the resource name.
  // This avoids a hardcoded module→queryKey mapping and stays DRY with TanStack Query.
  queryClient.invalidateQueries({
    predicate: (query) => {
      const keyString = JSON.stringify(query.queryKey);
      return keyString.includes(resource);
    },
  });

  if (import.meta.env?.DEV) {
    console.log("[Mercure] Workflow transition:", { eventType, resource, data });
  }
};

/**
 * Generate workflow Mercure subscriptions from user permissions.
 *
 * For each module the user has access to, subscribes to workflow topics
 * for every resource in that module × every accessible filiale.
 *
 * @param filialeCode       - User's primary filiale code (unused currently, reserved)
 * @param accessibleFiliales - Filiales the user can access
 * @param modules           - Module names the user has permissions for
 * @returns Array of Mercure subscription configs
 */
export function getWorkflowSubscriptions(
  filialeCode: string,
  accessibleFiliales: readonly string[],
  modules: readonly string[],
): Subscription[] {
  const subscriptions: Subscription[] = [];

  for (const mod of modules) {
    const resources = MODULE_WORKFLOW_RESOURCES[mod] ?? [];
    for (const resource of resources) {
      for (const filiale of accessibleFiliales) {
        const id = `workflow.${resource}.${filiale}`;
        subscriptions.push({
          id,
          topic: `/api/workflow/${resource}/${filiale}`,
          description: `Workflow transitions: ${resource} (${filiale})`,
          handler: workflowHandler,
        });
      }
    }
  }

  return subscriptions;
}
